import { set } from 'lodash';
import { getRoot } from 'mobx-easy';
import moment from 'moment-timezone';
import { escapeCSV } from '@bringg-frontend/utils';

import { timezoneProvider } from 'bringg-web';
import RootStore from 'bringg-web/stores/root-store';

enum ACTIONS {
	CREATE = 'create',
	UPDATE = 'update',
	DELETE = 'delete'
}

const FALSY_STRINGS = new Set(['false', 'null', '0', '']);

export function arrayToCSV(array: string[][], fileName: string): File {
	return new File([array.map(row => row.map(escapeCSV).join(',')).join('\n')], fileName);
}

export function csvToExclusionWindows(csv: string[][], headers: string[]) {
	return replaceHeaders(csv, headers).map(formatLine);
}

function dateTimeToISO8601(
	dateTime: unknown,
	dateTimeFormat: string,
	timezone: string,
	defaultDate: Date
): string | unknown {
	if (typeof dateTime === 'string') {
		const parsed = moment.tz(dateTime, dateTimeFormat, timezone);

		if (!parsed.isValid()) return dateTime;

		if (!dateTime.includes(':')) {
			// set default hours if there are no hours in the date time
			parsed.set({
				hour: defaultDate.getHours(),
				minute: defaultDate.getMinutes(),
				second: defaultDate.getSeconds(),
				millisecond: defaultDate.getMilliseconds()
			});
		}
		return parsed.toISOString();
	}

	return dateTime;
}

function getServiceAreaExternalIds(exclusionWindow: Record<string, unknown>): string[] | undefined {
	if (typeof exclusionWindow.service_area_external_ids === 'string') {
		return exclusionWindow.service_area_external_ids.split(',').map(s => s.trim());
	}

	if (typeof exclusionWindow.service_area_external_ids === 'number') {
		return [exclusionWindow.service_area_external_ids.toString()];
	}

	return undefined;
}

function getAction(exclusionWindow: Record<string, unknown>): string {
	if ('id' in exclusionWindow && exclusionWindow.id) {
		if ('delete' in exclusionWindow && exclusionWindow.delete) {
			return ACTIONS.DELETE;
		}

		return ACTIONS.UPDATE;
	}

	return ACTIONS.CREATE;
}

function getRepeat(exclusionWindow: Record<string, unknown>): boolean {
	if ('repeat' in exclusionWindow) {
		if (exclusionWindow.repeat === undefined || exclusionWindow.repeat === null) {
			return false;
		}

		const falsyString = exclusionWindow.repeat.toString().trim().toLowerCase();

		return !FALSY_STRINGS.has(falsyString);
	}

	return false;
}

export function exclusionWindowsToJSONL(
	exclusionWindows: Record<string, unknown>[],
	fileName: string,
	dateTimeFormat: string
): File {
	const teams = getRoot<RootStore>().data.teamsStore.all;

	const externalIdTeamIdMap = new Map<string, number>(teams.map(team => [team.external_id, team.id]));

	exclusionWindows.forEach(exclusionWindow => {
		if ('external_team_id' in exclusionWindow) {
			// team is required so always take timezone of the team
			const timezone = timezoneProvider.getTimezoneByTeamId(
				externalIdTeamIdMap.get(exclusionWindow.external_team_id?.toString())
			);

			exclusionWindow.start_time = dateTimeToISO8601(
				exclusionWindow.start_time,
				dateTimeFormat,
				timezone,
				moment().add(1, 'minute').toDate()
			);
			exclusionWindow.end_time = dateTimeToISO8601(
				exclusionWindow.end_time,
				dateTimeFormat,
				timezone,
				moment().endOf('day').toDate()
			);
		}
		exclusionWindow.action = getAction(exclusionWindow);
		exclusionWindow.repeat = getRepeat(exclusionWindow);
		exclusionWindow.service_area_external_ids = getServiceAreaExternalIds(exclusionWindow);
	});

	return new File(
		[exclusionWindows.map(exclusion_window => JSON.stringify({ exclusion_window })).join('\n')],
		fileName.replace('.csv', '.jsonl'),
		{
			type: 'application/octet-stream'
		}
	);
}

export function replaceHeaders(data: string[][], headers: string[]) {
	const rows = data.slice(1);

	return rows.map(row =>
		headers.reduce((acc, value, index) => {
			if (value === null) {
				return acc;
			}

			acc[value] = row[index];
			return acc;
		}, {})
	);
}

export function formatLine(parsedTask: Record<string, unknown>) {
	const task = Object.entries(parsedTask).reduce((acc, [keyString, value]) => {
		set(acc, keyString, value);
		return acc;
	}, {} as Record<string, unknown>);

	return task;
}
